import { QueryKeys } from "@/constants";
import { useNotificationError } from "@/context/error-provider";
import { ErrorNotificationType } from "@/pages/components/error-notification";
import httpClient from "@/services";
import { useMutation } from "@tanstack/react-query";
import type { AxiosError } from "axios";

type IPasswordReset = {
  username: string;
  groupId: string;
  loginGroupId: string;
  divisionId: string;
  enteredOTP?: string;
  password?: string;
  signature?: string;
};

const forgotUsername = async (userInfo: IAccountResetUsername) => {
  const url = "/login/forgotUsername";
  const data = {
    email: userInfo.email,
    groupId: userInfo.groupId,
    loginGroupId: userInfo.loginGroupId,
  };
  const config = {
    headers: {
      "Accept-Version": "1.0",
      "Content-Type": "application/json",
    },
  };
  return httpClient.post(url, data, config).then((res) => {
    return res.data;
  });
};

const passwordReset = async (userInfo: IPasswordReset) => {
  const url = "/login/passwordReset";
  const data = {
    username: userInfo.username,
    groupId: userInfo.groupId,
    loginGroupId: userInfo.loginGroupId,
    divisionId: userInfo.divisionId,
  };
  const config = {
    headers: {
      "Accept-Version": "1.0",
      "Content-Type": "application/json",
    },
  };
  return httpClient.post(url, data, config).then((res) => {
    return res.data;
  });
};

export const verifyForgotPasswordOTP = async (resetInfo: IPasswordReset) => {
  const url = "/otp/passwordReset";
  const data = {
    username: resetInfo.username,
    enteredOTP: resetInfo.enteredOTP,
    password: resetInfo.password,
    groupId: resetInfo.groupId,
    loginGroupId: resetInfo.loginGroupId,
    divisionId: resetInfo.divisionId,
  };
  const config = {
    headers: {
      "Accept-Version": "1.0",
      "Content-Type": "application/json",
      signature: `${resetInfo.signature}`,
    },
  };
  return httpClient.post(url, data, config).then((res) => {
    return res.data;
  });
};

export type IAccountResetResponse = {
  expiryTimestamp?: string;
  message: string;
  signature?: string;
  success: boolean;
  status: string;
  createdTimestamp?: string;
};

type IAccountResetUsername = {
  email: string;
  loginGroupId: string;
  groupId: string;
};
const usePasswordReset = () => {
  const { addError } = useNotificationError();
  return useMutation({
    mutationKey: [QueryKeys.PASSWORD_RESET],
    mutationFn: (userInfo: IPasswordReset) => passwordReset(userInfo),
    onSuccess: (data: IAccountResetResponse) => {
      if (!data.success) {
        addError({
          message: data.message,
          type: ErrorNotificationType.ERROR,
        });
      }
    },
    onError: (error: AxiosError) => {
      addError({
        message: error.message,
        type: ErrorNotificationType.ERROR,
      });
    },
  });
};

const useForgotUsername = () => {
  const { addError } = useNotificationError();
  return useMutation({
    mutationKey: [QueryKeys.FORGET_USERNAME],
    mutationFn: (userInfo: IAccountResetUsername) => forgotUsername(userInfo),
    onSuccess: (data: IAccountResetResponse) => {
      if (!data.success) {
        addError({
          message: data.message,
          type: ErrorNotificationType.ERROR,
        });
      } else {
        addError({
          message: data.message,
          type: ErrorNotificationType.INFO,
        });
      }
    },
    onError: (error: AxiosError) => {
      addError({
        message: error.message,
        type: ErrorNotificationType.ERROR,
      });
    },
  });
};

const useVerifyForgotPasswordOTP = () => {
  const { addError } = useNotificationError();
  return useMutation({
    mutationKey: [QueryKeys.FORGET_PASSWORD_OTP],
    mutationFn: (userInfo: IPasswordReset) => verifyForgotPasswordOTP(userInfo),
    onSuccess: (data: IAccountResetResponse) => {
      if (!data.success) {
        addError({
          message: data.message,
          type: ErrorNotificationType.ERROR,
        });
      } else {
        addError({
          message: data.message,
          type: ErrorNotificationType.INFO,
        });
      }
    },
    onError: (error: AxiosError) => {
      addError({
        message: error.message,
        type: ErrorNotificationType.ERROR,
      });
    },
  });
};

export { usePasswordReset, useForgotUsername, useVerifyForgotPasswordOTP };
